/**
 * AppShell Component
 * Main application layout with header, sidebar, content, and panels.
 * Wraps feature modules with ErrorBoundaries for resilience.
 */

import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { MainHeader } from "./MainHeader";
import { BodyContent } from "./BodyContent";
import {
  LeftMenu,
  NavHeader,
  NAV_FOCUS_TAB_ID,
  AppFooter,
  type UtilityTab,
  type LeftMenuButton,
  type ChatTabContext,
  type NavTab,
  type NavDockKind,
} from "kci-design-lib";
import { AIPanel, PANEL_DEFAULT_WIDTH } from "../ai/AIPanel";
import { NotificationPanel } from "../notifications/NotificationPanel";
import { UtilityPanel } from "../panels/UtilityPanel";
import { ErrorBoundary } from "../../components/ErrorBoundary/ErrorBoundary";
import {
  usePanels,
  useNavigation,
  useEngagement,
} from "../../stores/useAppStore";
import { useT } from "../../i18n/I18nProvider";
import { CONFIG } from "../../constants";
import { environment } from "../../environments/environment";
import { getStandaloneContext } from "../clara-ai-chat/constants";
import { usePreferencesContext } from "../../context/PreferencesContext";

import {
  fetchFooterContent,
  type FooterContent,
} from "../../services/footerService";
import { FeedbackModal } from "./FeedbackModal";
import {
  fetchMainHeaderActions,
  type MainHeaderAction,
} from "../../services/mainHeaderService";
import {
  fetchDockButtons,
  isMaintenanceModeEnabled,
} from "../../services/dockService";
import type { DockButtonConfig } from "../../services/dockService";
import type { UtilityPanelMode } from "../../types";
import {
  DockFlyout,
  DialogBox,
  Notification,
  triggerNotification,
} from "kci-design-lib";
import { useDockFlyout } from "../../hooks/useDockFlyout";
import { useApplicationDisclaimer, useApplicationSettings } from "../../hooks";
import { useMsalAuth } from "../../auth/useMsalAuth";
import { renderDisclaimerContent } from "../../components/Disclaimer/DisclaimerContent";
import { kciEventBus } from "../../eventBus/KCiEventBus";
import {
  AGENT_DASHBOARD_LABEL,
  AGENT_DASHBOARD_TAB_ID,
  useNavTabs,
} from "../../hooks/useNavTabs";
import type { AssetItem } from "../workspace/assetmodel";

import { FileViewerContext } from "../../contexts/FileViewerContext";
import {
  WORKSPACE_ASSET_EVENTS,
  createAssetViewRequestedEnvelope,
  type AssetFileViewPayload,
  type AssetRequestContext,
} from "../workspace/workspaceAssetEvents";
import { KCI_APP_IDS } from "../../eventBus/appIds";
import type { ContextTab } from "../panels/bodies/ContextPanelBody";
import {
  resolveTabClaraContext,
  resolveWorkspaceIdentity,
  resolveWorkspaceTabGrouping,
} from "../../services/tabClaraContext";
import onboardingTourService from "../../services/onboardingTourService";
import { TourOverlay } from "./TourOverlay";
import {
  getAgentSharedUserIds,
  lookupUserEmails,
} from "../../services/tabManagementService";

// Validation errors that are returned before any optimistic state update fires.
const PRE_OPTIMISTIC_ERROR_MESSAGES = new Set([
  "Title is required",
  "Title already exists",
  "Only the owner can delete this agent",
]);
const FEEDBACK_TOAST_ID = "feedback-success-toast";
const AGENT_SHARE_TOAST_ID = "agent-share-success-toast";
const RECENT_TAB_RENAME_TOAST_ID = "recent-tab-rename-success-toast";

const LEFT_WIZARD_CHAT_HISTORY_FEATURE_KEY = "left-wizard-icons";
const NEW_RECENT_TABS_FEATURE_KEY = "new-recent-tabs";
const LEFT_WIZARD_CHAT_HISTORY_TOUR_ID = "left-wizard-chat-history-icon";
const LEFT_WIZARD_WORKSPACE_TOUR_ID = "left-wizard-workspace-icon";
const LEFT_WIZARD_AGENTS_TOUR_ID = "left-wizard-agents-icon";
const EXECUTION_STREAM_DOCK_BUTTON: DockButtonConfig = {
  id: "execution-stream",
  titleKey: "execution-stream",
  label: "Execution Stream",
  kind: "workspaces",
  icon: "conversion_path",
  iconMode: "material-symbol",
  homePage: "execution-stream",
  actionType: "navigate",
  order: 1000,
};

function isExecutionStreamDockButton(button: Pick<DockButtonConfig, "id" | "homePage">): boolean {
  return [button.id, button.homePage].some(
    (value) => value === "execution-stream" || value === "__execution-stream__",
  );
}

function withExecutionStreamDockButton(buttons: DockButtonConfig[]): DockButtonConfig[] {
  if (!isMaintenanceModeEnabled()) {
    return buttons.filter((button) => !isExecutionStreamDockButton(button));
  }

  if (buttons.some(isExecutionStreamDockButton)) {
    return buttons;
  }

  return [...buttons, EXECUTION_STREAM_DOCK_BUTTON];
}

function isForShellTarget(target: string | string[] | undefined): boolean {
  if (!target) {
    return true;
  }

  if (Array.isArray(target)) {
    return target.includes(KCI_APP_IDS.SHELL);
  }

  return target === KCI_APP_IDS.SHELL;
}

export const AppShell: React.FC = () => {
  const t = useT();
  const fallbackFooterText = t("header.appname");
  const { account } = useMsalAuth();
  const userId = account?.localAccountId ?? account?.homeAccountId ?? "";
  const {
    disclaimerContent: firstLoginDisclaimerContent,
    isLoading: firstLoginDisclaimerLoading,
    isAcknowledging: firstLoginAcknowledging,
    hasAcknowledged,
    acknowledge: acknowledgeFirstLoginDisclaimer,
  } = useApplicationDisclaimer(userId);
  const { applicationSettings } = useApplicationSettings();
  const allowAgentSharing = Boolean(applicationSettings?.allowAgentSharing);
  // Which nav tabs currently have the "Ask KCi & Team" panel open — a Set
  // rather than a single boolean so the panel is scoped per-tab: switching
  // tabs hides it unless the NEW tab is also in this set, and switching
  // back restores it. See `aiPanelOpen` (derived below) for the per-render
  // "is it open for the active tab right now" boolean.
  const [aiPanelOpenTabIds, setAiPanelOpenTabIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const [assetsPanelOpen, setAssetsPanelOpen] = useState(false);
  const [milestonePanelOpen, setMilestonePanelOpen] = useState(false);
  const [aiPanelWidth, setAiPanelWidth] = useState<number>(PANEL_DEFAULT_WIDTH);
  const [aiPanelCollapsedTabIds, setAiPanelCollapsedTabIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const [aiPanelSessionId, setAiPanelSessionId] = useState<string | null>(null);
  const [fullscreenAiTabByOriginTabId, setFullscreenAiTabByOriginTabId] =
    useState<ReadonlyMap<string, string>>(() => new Map());
  const fullscreenOriginTabIdsRef = useRef<Map<string, string>>(new Map());
  // Stable map: tabId → AssetItem, shared via FileViewerContext with BodyContent
  const fileViewerFilesRef = useRef<Map<string, AssetItem>>(new Map());
  const [dockButtons, setDockButtons] = useState<DockButtonConfig[]>(() =>
    isMaintenanceModeEnabled() ? [EXECUTION_STREAM_DOCK_BUTTON] : [],
  );
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [headerActions, setHeaderActions] = useState<MainHeaderAction[]>([]);
  const [footerData, setFooterData] = useState<FooterContent | null>(null);
  const [selectedDockKindOverride, setSelectedDockKindOverride] =
    useState<NavDockKind | null>(null);
  const hasStartedLeftWizardTourRef = useRef(false);
  const hasStartedNewRecentTabsTourRef = useRef(false);
  const isWelcomeTutorialActiveRef = useRef(false);
  // Set to true when the first-login disclaimer dialog is rendered in this
  // session (i.e. the user has never accepted it before).  Used to gate the
  // post-acceptance tour dispatch so it only fires for genuine first-time users
  // and not on every subsequent page load where hasAcknowledged is already true.
  const firstLoginDisclaimerShownRef = useRef(false);
  // Track when the first-login disclaimer dialog is actually displayed.
  useEffect(() => {
    if (
      !firstLoginDisclaimerLoading &&
      !hasAcknowledged &&
      !!firstLoginDisclaimerContent
    ) {
      firstLoginDisclaimerShownRef.current = true;
      // Tell the Angular workspace layer not to auto-start any tours until
      // the user has accepted this disclaimer.  The flag is stored in
      // sessionStorage so it persists across the brief React re-renders
      // during the acceptance flow but is cleared automatically when the
      // browser tab is closed (unlike localStorage).
      sessionStorage.setItem("kci.first-login-disclaimer.pending", "true");
    }
  }, [
    firstLoginDisclaimerLoading,
    hasAcknowledged,
    firstLoginDisclaimerContent,
  ]);

  const {
    navTabs,
    activeNavTabId,
    activeNavTabContext,
    recentNavTabs,
    collapsedGroupIds,
    activateNavTab,
    handleNavTabClose,
    handleNavAddAction,
    handleRecentNavTabOpen,
    handleRecentNavTabPinToggle,
    handleRecentNavTabAction,
    handleWorkspaceSecondaryTabOpen,
    handleChatItemTabOpen,
  
    handleDashboardNewChatAction,
    handleDashboardNewAgentAction,
    onToggleGroupCollapse,
    handleOpenReviewBalanceTab,
    handleOpenInTab,
    openOrFocusNavTab,
    pendingAgentBuilderNavAction,
    confirmAgentBuilderNavAction,
    cancelAgentBuilderNavAction,
    isAgentBuilderNavGuardActive,
    queuePendingAgentBuilderAction,
  } = useNavTabs();

  // Whether the AI panel is open for the CURRENTLY ACTIVE tab specifically —
  // this is what actually drives visibility, not aiPanelOpenTabIds itself.
  const aiPanelOpen = aiPanelOpenTabIds.has(activeNavTabId);
  const aiPanelCollapsed = aiPanelCollapsedTabIds.has(activeNavTabId);
  const fullscreenAiTabForActiveTab =
    fullscreenAiTabByOriginTabId.get(activeNavTabId) ?? null;
  const hasFullscreenAiForActiveTab = Boolean(fullscreenAiTabForActiveTab);

  // Active utility-panel tab. Drives both the `--active` styling on the
  // three nav-header utility buttons and the rendered <UtilityPanel> body.
  // when a tab is selected.
  const [utilityTab, setUtilityTab] = useState<UtilityTab>(null);
  const [contextTabOverride, setContextTabOverride] = useState<ContextTab | null>(null);
  const claraContextOverrideRef = useRef<Array<{
    tabId: string;
    context: Array<{ key: string; value: unknown }>;
  }>>([]);

  // const t = useT();
  const { navigation, navigateToPage } = useNavigation();
  const { setEngagement } = useEngagement();
  const { preferences } = usePreferencesContext();
  const isStartPage =
    activeNavTabId === NAV_FOCUS_TAB_ID &&
    navigation.currentApp === CONFIG.CLARA_APP &&
    navigation.currentPage === CONFIG.FIXED_TAB_CLARA_AI_CHAT &&
    navigation.currentSubPage === null;

  // Whether a Clara AI Angular remote is ALREADY mounted full-page in the
  // main body (New Chat tab, a Chat-History-opened session tab — both use
  // FIXED_TAB_CLARA_AI_CHAT — or Agent Dashboard). The AI panel is a sibling
  // of that body content, so it would mount a SECOND <clara-ai-chat-element>
  // alongside it — and since the Angular remote's Router and its
  // window-level event listeners (e.g. kci-load-chat-session) are ONE
  // page-wide singleton shared by every mounted instance, two at once
  // doubles those listeners and races the chat-session-loading logic,
  // breaking Chat History's "open session in a new tab" feature. Also drives
  // hiding the nav-bar spark icon (pointless there anyway).
  const isClaraAiModuleActive =
    navigation.currentApp === "angular-remote" ||
    navigation.currentApp === "kci-agents" ||
    (navigation.currentApp === "tru-clara-app" &&
      navigation.currentPage === CONFIG.FIXED_TAB_CLARA_AI_CHAT);
  //const activeNavTab = navTabs.find((tab) => tab.id === activeNavTabId);
  const isFullscreenAiPanelTab =
    navigation.currentApp === CONFIG.CLARA_APP &&
    (activeNavTabId.startsWith("ai-chat-") ||
       navigation.currentPage.startsWith("ai-chat-"));

  const publishCurrentClaraContext = useCallback(() => {
    const activeTab = navTabs.find((tab) => tab.id === activeNavTabId);
    const isWorkspaceChat =
      activeTab?.tabKind === 'workspaces' &&
      navigation.currentPage === CONFIG.FIXED_TAB_HOME;
    const isStandaloneChat =
      navigation.currentPage === CONFIG.FIXED_TAB_CLARA_AI_CHAT;

    const contextOverride = claraContextOverrideRef.current.find(
      ({ tabId }) => tabId === activeNavTabId,
    );
    let context: Array<{ key: string; value: unknown }>;

    if (isWorkspaceChat) {
      context = resolveTabClaraContext(activeNavTabContext, contextOverride?.context);
      if (context.length === 0) return;
    } else if (isStandaloneChat) {
      context = resolveTabClaraContext(activeNavTabContext, contextOverride?.context);
      if (context.length === 0) {
        context = [
          ...getStandaloneContext(preferences.language),
          { key: 'aiIntegrationId', value: environment.aiIntegrationId },
        ];
      }
    } else {
      return;
    }

    kciEventBus.publish(
      'ClaraContextUpdated',
      {
        source: KCI_APP_IDS.SHELL,
        trigger: 'askKciPanel',
        context,
        clickedAt: Date.now(),
      },
      {
        source: KCI_APP_IDS.SHELL,
        target: [KCI_APP_IDS.SHELL, KCI_APP_IDS.CLARA_REMOTE],
      },
    );
  }, [activeNavTabContext, activeNavTabId, navigation.currentPage, navTabs, preferences.language]);

  const publishCommonClaraContext = useCallback(() => {
    const contextOverride = claraContextOverrideRef.current.find(
      ({ tabId }) => tabId === activeNavTabId,
    );
    let context = contextOverride?.tabId === activeNavTabId
      ? contextOverride.context
      : [];
    if (context.length === 0) {
      context = [
        ...getStandaloneContext(preferences.language),
        { key: 'aiIntegrationId', value: environment.aiIntegrationId },
      ];
    }
    
    kciEventBus.publish(
      "ClaraContextUpdated",
      {
        source: KCI_APP_IDS.SHELL,
        trigger: "aiSpark",
        context,
        clickedAt: Date.now(),
      },
      {
        source: KCI_APP_IDS.SHELL,
        target: [KCI_APP_IDS.SHELL, KCI_APP_IDS.CLARA_REMOTE],
      },
    );
  }, [activeNavTabId, preferences.language]);

  // Refresh Clara's shared session storage whenever the user switches between
  // standalone and workspace chat, even when the AI panel is already open.
  useEffect(() => {
    publishCurrentClaraContext();
  }, [publishCurrentClaraContext]);

  const dockFlyout = useDockFlyout({
    onWorkspaceSecondaryItemSelect: handleWorkspaceSecondaryTabOpen,
    onChatItemSelect: handleChatItemTabOpen,
    onNewChatActionSelect: handleDashboardNewChatAction,
    onNewAgentActionSelect: handleDashboardNewAgentAction,
  });

  // Listen for the kci:openReviewBalance event dispatched by kci-workspace
  // when the user clicks "Review Balance" in Configuration and Setup.
  useEffect(() => {
    function handleOpenReviewBalance(event: Event) {
      const detail = (
        event as CustomEvent<{ storageKey: string; datasetLabel: string }>
      ).detail;
      if (detail?.storageKey) {
        handleOpenReviewBalanceTab(
          detail.storageKey,
          detail.datasetLabel ?? "",
        );
      }
    }
    window.addEventListener("kci:openReviewBalance", handleOpenReviewBalance);
    return () =>
      window.removeEventListener(
        "kci:openReviewBalance",
        handleOpenReviewBalance,
      );
  }, [handleOpenReviewBalanceTab]);

  // Listen for the kci:openInTab event dispatched by kci-workspace components
  // when the user clicks "View in tab" (e.g. Data-Driven Insights, Data-Driven Assessment).
  useEffect(() => {
    function handleKciOpenInTab(event: Event) {
      const detail = (
        event as CustomEvent<{
          contentType: string;
          storageKey: string;
          tabLabel: string;
        }>
      ).detail;

      if (detail?.contentType && detail.storageKey) {
        handleOpenInTab(detail);
      }
    }
    window.addEventListener("kci:openInTab", handleKciOpenInTab);
    return () =>
      window.removeEventListener("kci:openInTab", handleKciOpenInTab);
  }, [handleOpenInTab]);

  // Listen for the kci:closeTab event dispatched by full-page tab components
  // (e.g. DataDrivenAssessmentPage) when the user clicks the Close button inside
  // a tab — the tab shell has no other way to know it should close itself.
  useEffect(() => {
    function handleKciCloseTab(event: Event) {
      const detail = (event as CustomEvent<{ tabId: string }>).detail;
      if (detail?.tabId) {
        claraContextOverrideRef.current = claraContextOverrideRef.current.filter(
          ({ tabId }) => tabId !== detail.tabId,
        );
        handleNavTabClose(detail.tabId);
      }
    }
    window.addEventListener("kci:closeTab", handleKciCloseTab);
    return () => window.removeEventListener("kci:closeTab", handleKciCloseTab);
  }, [handleNavTabClose]);

  // Wraps the recent-tab action handler to keep the DockFlyout in sync.
  // Notify the flyout whenever the optimistic local-state update fired, which
  // is all cases except the pre-optimistic validation rejections.
  const handleRecentNavTabActionWithSync = useCallback(
    async (
      entry: Parameters<typeof handleRecentNavTabAction>[0],
      actionId: Parameters<typeof handleRecentNavTabAction>[1],
      payload?: Parameters<typeof handleRecentNavTabAction>[2],
    ) => {
      const result = await handleRecentNavTabAction(entry, actionId, payload);

      if (actionId === "share" && result.status === "success") {
        triggerNotification(AGENT_SHARE_TOAST_ID);
      }

      if (actionId === "rename" && result.status === "success") {
        triggerNotification(RECENT_TAB_RENAME_TOAST_ID);
      }

      const isPreOptimisticError =
        result.status === "error" &&
        (PRE_OPTIMISTIC_ERROR_MESSAGES.has(result.message ?? "") ||
          result.message?.startsWith("Title must be"));

      if (!isPreOptimisticError) {
        if (actionId === "rename" || actionId === "delete") {
          dockFlyout.refreshData();
        }
      }
      return result;
    },
    [handleRecentNavTabAction, dockFlyout],
  );

  const { panels, setUtilityPanelMode } = usePanels();

  // Derive mode from local state for immediate render, or fall back to store
  // for modes that come from other sources (ws-chats, project-chats, etc.)
  const derivedMode: UtilityPanelMode =
    utilityTab === null
      ? null
      : utilityTab === "context"
        ? "context"
        : utilityTab === "reasoning"
          ? "reasoning"
          : "activity";

  const effectiveMode = derivedMode ?? panels.utilityPanelMode;

  // Sync local state changes to store
  useEffect(() => {
    setUtilityPanelMode(derivedMode);
  }, [utilityTab, setUtilityPanelMode, derivedMode]);

  useEffect(() => {
    const unsubscribe = kciEventBus.subscribe(
      "ContextButtonClicked",
      (event) => {
        if (!isForShellTarget(event.target)) {
          return;
        }

        const payload = event.payload as
          | {
              trigger?: string;
              resourceType?: string;
              notificationRoute?: string;
            }
          | undefined;
        const trigger = payload?.trigger;
        if (trigger !== "notification") {
          return;
        }

        const notificationRoute = String(
          payload?.notificationRoute ?? "",
        ).toLowerCase();
        const isSharedRoute = notificationRoute.startsWith("shared-");
        const legacyResourceType = String(
          payload?.resourceType ?? "",
        ).toLowerCase();

        if (
          isSharedRoute ||
          legacyResourceType === "agent" ||
          legacyResourceType === "prompt"
        ) {
          // For shared agent/prompt notifications, let Clara own the panel flow
          // so Accept/Decline opens once without a second KCI context panel.
          setUtilityTab(null);
          setContextTabOverride(null);
          setNotifPanelOpen(false);
          return;
        }

        setUtilityTab("context");
        setContextTabOverride("prompts");
        setNotifPanelOpen(false);

        // One-shot override: after the context tab is forced to Prompts,
        // clear the override so normal context opens keep default behavior.
        window.setTimeout(() => {
          setContextTabOverride(null);
        }, 0);
      },
    );

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    fetchDockButtons()
      .then((buttons) => setDockButtons(withExecutionStreamDockButton(buttons)))
      .catch((error) => {
        console.error("Failed to load dock buttons", error);
      });
  }, []);

  useEffect(() => {
    void onboardingTourService.loadRegistryTours("TourGuide", "en-US");
  }, []);

  useEffect(() => {
    fetchMainHeaderActions()
      .then(setHeaderActions)
      .catch((error) => {
        console.error("Failed to load main header actions", error);
      });
  }, []);

  useEffect(() => {
    let isActive = true;
    const currentLocale = preferences.language?.includes("-")
      ? preferences.language
      : `${preferences.language ?? "en"}-${(preferences.language ?? "en").toUpperCase()}`;
    const footerPayload = [
      { key: "Language", value: currentLocale },
      { key: "ctry", value: currentLocale.split("-")[1] ?? "US" },
      { key: "SCREEN.TYPE", value: "Standalone" },
    ];

    fetchFooterContent(currentLocale, footerPayload)
      .then((data) => {
        if (isActive) {
          setFooterData(data);
        }
      })
      .catch((error) => {
        if (isActive) {
          console.error("Failed to load footer content", error);
        }
      });

    return () => {
      isActive = false;
    };
  }, [preferences.language]);

  /**
   * Capture the actual clicked DOM button before LeftMenu calls onButtonClick.
   * This lets DockFlyout position itself relative to the trigger.
   */
  const handleLeftMenuClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as Element | null;
      const triggerEl = target?.closest("button") as HTMLElement | null;

      if (triggerEl) {
        dockFlyout.registerTriggerClick(triggerEl);
      }
    },
    [dockFlyout],
  );

  const dockButtonDefs = useMemo(() => {
    const getTourIdByKind = (
      kind: DockButtonConfig["kind"],
    ): string | undefined => {
      if (kind === "chats") return LEFT_WIZARD_CHAT_HISTORY_TOUR_ID;
      if (kind === "workspaces") return LEFT_WIZARD_WORKSPACE_TOUR_ID;
      if (kind === "agents") return LEFT_WIZARD_AGENTS_TOUR_ID;
      return undefined;
    };

    return dockButtons.map((btn) => {
      const isWorkspaces = btn.kind === "workspaces";
      const isExecutionStream = isExecutionStreamDockButton(btn);
      const isNavigationActive =
        (isExecutionStream &&
          navigation.currentApp === CONFIG.WORKSPACE &&
          navigation.currentPage === "__execution-stream__") ||
        (navigation.currentApp === btn.id &&
          (navigation.currentPage === btn.homePage ||
            (isWorkspaces && navigation.currentPage === CONFIG.FIXED_TAB_HOME)));
      const isDockOverrideActive =
        selectedDockKindOverride !== null &&
        btn.kind === selectedDockKindOverride &&
        (isExecutionStream
          ? navigation.currentPage === "__execution-stream__"
          : navigation.currentPage !== "__execution-stream__");
      const isFlyoutActiveForBtn =
        dockFlyout.isOpen && dockFlyout.flyoutKind === btn.kind;
      const isActive =
        selectedDockKindOverride !== null
          ? isDockOverrideActive
          : isFlyoutActiveForBtn || isNavigationActive;

      const translatedTitle = t(btn.titleKey);
      const resolvedLabel =
        btn.label?.trim() ||
        (translatedTitle && translatedTitle !== btn.titleKey
          ? translatedTitle
          : "") ||
        translatedTitle ||
        btn.kind;
      return {
        id: btn.id,
        icon: btn.icon,
        iconMode: btn.iconMode,
        iconClassName: btn.iconClassName,
        iconFallback: btn.iconFallback,
        label: resolvedLabel,
        active: isActive,
        showBlueDot: btn.showBlueDot,
        showRedDot: btn.showRedDot,
        homePage: btn.homePage,
        tourId: getTourIdByKind(btn.kind),
      } as LeftMenuButton;
    });
  }, [
    dockButtons,
    navigation.currentApp,
    navigation.currentPage,
    selectedDockKindOverride,
    dockFlyout.isOpen,
    dockFlyout.flyoutKind,
    t,
  ]);

  const handleStartLeftMenuTour = useCallback(async () => {
    if (hasStartedLeftWizardTourRef.current) {
      return;
    }

    hasStartedLeftWizardTourRef.current = true;
    isWelcomeTutorialActiveRef.current = true;
    await onboardingTourService.startFeatureTour(
      LEFT_WIZARD_CHAT_HISTORY_FEATURE_KEY,
    );
  }, []);

  const handleNewRecentTabsTourTrigger = useCallback(async () => {
    if (hasStartedNewRecentTabsTourRef.current) {
      return;
    }

    hasStartedNewRecentTabsTourRef.current = true;
    await onboardingTourService.startFeatureTour(NEW_RECENT_TABS_FEATURE_KEY);
  }, []);

  const handleActiveNavTabChange = useCallback((tab: NavTab | null) => {
    setSelectedDockKindOverride(tab?.tabKind ?? null);
  }, []);

  const handleDockButtonClick = useCallback(
    (button: LeftMenuButton) => {
      console.log(
        "[AppShell] Dock button clicked:",
        button.id,
        "homePage:",
        button.homePage,
      );
      const clickedDockConfig = dockButtons.find((dockButton) => {
        if (dockButton.id !== button.id) {
          return false;
        }
        // Some LeftMenu implementations may omit custom fields like homePage
        // in the click payload. Fall back to id-only match when it is missing.
        if (!button.homePage) {
          return true;
        }
        return dockButton.homePage === button.homePage;
      });

      if (!clickedDockConfig) {
        return;
      }

      if (
        clickedDockConfig.actionType === "flyout" &&
        clickedDockConfig.flyout
      ) {
        dockFlyout.toggleFlyout(clickedDockConfig);
        return;
      }

      const executeNavigation = () => {
        dockFlyout.closeFlyout();

        if (isExecutionStreamDockButton(clickedDockConfig)) {
          setSelectedDockKindOverride(null);
          setEngagement(null);
          openOrFocusNavTab({
            id: "execution-stream",
            label: clickedDockConfig.label?.trim() || "Execution Stream",
            removable: true,
            tabKind: "workspaces",
          });
          return;
        }

        if (clickedDockConfig.kind === "agents") {
          setSelectedDockKindOverride("agents");
          openOrFocusNavTab({
            id: AGENT_DASHBOARD_TAB_ID,
            label: AGENT_DASHBOARD_LABEL,
            removable: true,
            tabKind: "agents",
          });
          return;
        }

        // Normal navigation buttons
        setSelectedDockKindOverride(null);
        setEngagement(null);
        navigateToPage(
          button.id,
          button.homePage ?? clickedDockConfig.homePage ?? CONFIG.FIXED_TAB_HOME,
        );
      };

      if (isAgentBuilderNavGuardActive()) {
        queuePendingAgentBuilderAction({
          kind: "dock-navigation",
          execute: executeNavigation,
        });
        return;
      }

      executeNavigation();
    },
    [dockButtons, dockFlyout, isAgentBuilderNavGuardActive, navigateToPage, openOrFocusNavTab, queuePendingAgentBuilderAction, setEngagement],
  );
  const closeAiPanelForActiveTab = useCallback(() => {
    setAiPanelSessionId(null);
    setAiPanelOpenTabIds((prev) => {
      if (!prev.has(activeNavTabId)) return prev;
      const next = new Set(prev);
      next.delete(activeNavTabId);
      return next;
    });
  }, [activeNavTabId]);

  const setAiPanelCollapsedForActiveTab = useCallback(
    (collapsed: boolean) => {
      setAiPanelCollapsedTabIds((prev) => {
        const isCurrentlyCollapsed = prev.has(activeNavTabId);
        if (isCurrentlyCollapsed === collapsed) return prev;

        const next = new Set(prev);
        if (collapsed) {
          next.add(activeNavTabId);
        } else {
          next.delete(activeNavTabId);
        }
        return next;
      });
    },
    [activeNavTabId],
  );

  const toggleAiPanel = useCallback(() => {
    // Defensive: the spark icon that calls this is already hidden while
    // isClaraAiModuleActive is true, but guard here too since this is also
    // reachable in principle without going through that button.
    if (isClaraAiModuleActive) return;

    const fullscreenTabId = fullscreenAiTabByOriginTabId.get(activeNavTabId);
    if (fullscreenTabId && navTabs.some((tab) => tab.id === fullscreenTabId)) {
      activateNavTab(fullscreenTabId, { trackFocus: true });
      return;
    }

    if (aiPanelOpen) return;

    publishCommonClaraContext();

    kciEventBus.publish(
      "kci.ai.openPanel",
      {
        source: KCI_APP_IDS.SHELL,
        trigger: "aiSpark",
        tabId: activeNavTabId,
        clickedAt: Date.now(),
      },
      {
        source: KCI_APP_IDS.SHELL,
        target: KCI_APP_IDS.WORKSPACE_APP,
      },
    );

    setAiPanelOpenTabIds((prev) => {
      const next = new Set(prev);
      next.add(activeNavTabId);
      return next;
    });
    if (notifPanelOpen) setNotifPanelOpen(false);
    if (feedbackModalOpen) setFeedbackModalOpen(false);
  }, [
    activeNavTabId,
    activateNavTab,
    aiPanelOpen,
    feedbackModalOpen,
    fullscreenAiTabByOriginTabId,
    isClaraAiModuleActive,
    navTabs,
    notifPanelOpen,
    publishCommonClaraContext,
  ]);

  // Opens the Ask KCi & Team panel for the CURRENTLY ACTIVE tab and, when a
  // context dictionary is supplied (e.g. by kci-workspace, a separate MFE
  // bundle), forwards it to the Clara AI Angular remote over the same
  // cross-MFE bus already used by NotificationPanel's ContextButtonClicked
  // publish.
  const openAiPanelWithContext = useCallback(
    (context?: Array<{ key: string; value: unknown }>, targetTabId = activeNavTabId) => {
      // See isClaraAiModuleActive above — never mount a second Clara AI
      // instance alongside one already showing full-page in the main body.
      if (isClaraAiModuleActive) return;

      const fullscreenTabId = fullscreenAiTabByOriginTabId.get(targetTabId);
      if (fullscreenTabId && navTabs.some((tab) => tab.id === fullscreenTabId)) {
        if (context && context.length > 0) {
          kciEventBus.publish(
            'ClaraContextUpdated',
            {
              source: KCI_APP_IDS.SHELL,
              trigger: 'askKciPanel',
              context,
              clickedAt: Date.now(),
            },
            {
              source: KCI_APP_IDS.SHELL,
              target: [KCI_APP_IDS.SHELL, KCI_APP_IDS.CLARA_REMOTE],
            },
          );
        }
        activateNavTab(fullscreenTabId, { trackFocus: true });
        return;
      }

      setAiPanelOpenTabIds((prev) => {
        if (prev.has(targetTabId)) return prev;
        const next = new Set(prev);
        next.add(targetTabId);
        return next;
      });
      if (notifPanelOpen) setNotifPanelOpen(false);
      if (feedbackModalOpen) setFeedbackModalOpen(false);

      if (context && context.length > 0) {
        const eventPayload = {
          source: KCI_APP_IDS.SHELL,
          trigger: 'askKciPanel',
          context,
          clickedAt: Date.now(),
        };
        const eventOptions = {
          source: KCI_APP_IDS.SHELL,
          target: [KCI_APP_IDS.SHELL, KCI_APP_IDS.CLARA_REMOTE],
        };

        // RemotePage replays this event when the panel finishes mounting.
        kciEventBus.publish('ClaraContextUpdated', eventPayload, eventOptions);
      }
    },
    [activeNavTabId, activateNavTab, feedbackModalOpen, fullscreenAiTabByOriginTabId, isClaraAiModuleActive, navTabs, notifPanelOpen],
  );

  const handleOpenAiInNewTab = useCallback((sessionId: string | null) => {
    const parentTabId = activeNavTabId;
    const originTabId = activeNavTabId;
    const existingFullscreenTabId = fullscreenAiTabByOriginTabId.get(originTabId);
    if (existingFullscreenTabId && navTabs.some((tab) => tab.id === existingFullscreenTabId)) {
      closeAiPanelForActiveTab();
      activateNavTab(existingFullscreenTabId, { trackFocus: true });
      return;
    }

    closeAiPanelForActiveTab();
    const tabId = `ai-chat-${Date.now()}`;
    const title = "Ask KCi";
    const explicitParentContext = claraContextOverrideRef.current.find(
      ({ tabId }) => tabId === parentTabId,
    )?.context;
    const parentContext = resolveTabClaraContext(activeNavTabContext, explicitParentContext);
    const workspaceIdentity = resolveWorkspaceIdentity(activeNavTabContext, parentContext);
    const tabContext: ChatTabContext = {
      kind: "chat",
      sessionId: null,
      title,
      sourceTabId: originTabId,
      ...(workspaceIdentity ?? {}),
    };
    const activeTab = navTabs.find((tab) => tab.id === activeNavTabId) ?? null;
    const sourceTab = activeNavTabContext?.kind === "chat" && activeNavTabContext.sourceTabId
      ? navTabs.find((tab) => tab.id === activeNavTabContext.sourceTabId) ?? activeTab
      : activeTab;
    const workspaceGrouping = resolveWorkspaceTabGrouping(activeNavTabContext, sourceTab, parentContext);

    if (parentContext?.length) {
      claraContextOverrideRef.current = [
        ...claraContextOverrideRef.current.filter(
          (override) => override.tabId !== tabId,
        ),
        { tabId, context: parentContext },
      ];
    }

    if (sessionId) {
      sessionStorage.setItem(`kci.fullscreenAiSession.${tabId}`, sessionId);
    }

    fullscreenOriginTabIdsRef.current.set(tabId, originTabId);
    setFullscreenAiTabByOriginTabId((prev) => {
      const next = new Map(prev);
      next.set(originTabId, tabId);
      return next;
    });
    openOrFocusNavTab({
      id: tabId,
      label: title,
      removable: true,
      tabKind: "chats",
      context: tabContext,
      parentTabId: sourceTab?.id ?? originTabId,
      ...(workspaceGrouping ?? {}),
    });
  }, [activeNavTabContext, activeNavTabId, activateNavTab, closeAiPanelForActiveTab, fullscreenAiTabByOriginTabId, navTabs, openOrFocusNavTab]);

  useEffect(() => {
    const handleRestoreAiPanel = (event: Event) => {
      const detail = (event as CustomEvent<{ tabId?: string }>).detail;
      const fullscreenTabId = detail?.tabId;
      if (!fullscreenTabId) return;

      const originTabId = fullscreenOriginTabIdsRef.current.get(fullscreenTabId);
      if (!originTabId) return;

      const requestId = `clara-session-${Date.now()}`;
      const storedSessionId = sessionStorage.getItem(`kci.fullscreenAiSession.${fullscreenTabId}`);
      let completed = false;
      const timeoutIdRef: { current: number | undefined } = { current: undefined };

      const restorePanel = (sessionId: string | null) => {
        if (completed) return;
        completed = true;
        if (timeoutIdRef.current !== undefined) {
          window.clearTimeout(timeoutIdRef.current);
        }
        window.removeEventListener('kci:active-chat-session', handleSession);
        fullscreenOriginTabIdsRef.current.delete(fullscreenTabId);
        sessionStorage.removeItem(`kci.fullscreenAiSession.${fullscreenTabId}`);
        setAiPanelSessionId(sessionId);
        handleNavTabClose(fullscreenTabId);
        setAiPanelCollapsedTabIds((prev) => {
          if (!prev.has(originTabId)) return prev;
          const next = new Set(prev);
          next.delete(originTabId);
          return next;
        });
        setFullscreenAiTabByOriginTabId((prev) => {
          if (!prev.has(originTabId)) return prev;
          const next = new Map(prev);
          next.delete(originTabId);
          return next;
        });
        setAiPanelOpenTabIds((prev) => {
          const next = new Set(prev);
          next.add(originTabId);
          return next;
        });
        activateNavTab(originTabId, { trackFocus: true });
      };

      const handleSession = (sessionEvent: Event) => {
        const sessionDetail = (sessionEvent as CustomEvent<{
          requestId?: string;
          sessionId?: string | null;
        }>).detail;
        if (sessionDetail?.requestId === requestId) {
          restorePanel(sessionDetail.sessionId ?? null);
        }
      };

      window.addEventListener('kci:active-chat-session', handleSession);
      window.dispatchEvent(
        new CustomEvent('kci:request-active-chat-session', { detail: { requestId } }),
      );
      timeoutIdRef.current = window.setTimeout(() => restorePanel(storedSessionId), 300);
    };

    window.addEventListener('kci:restoreAiPanel', handleRestoreAiPanel);
    return () => window.removeEventListener('kci:restoreAiPanel', handleRestoreAiPanel);
  }, [activateNavTab, handleNavTabClose]);

  // Listen for the kci:openAiPanel event dispatched by kci-workspace (or any
  // other MFE) when its own "Ask KCi & Team" trigger is clicked.
  useEffect(() => {
    function handleOpenAiPanel(event: Event) {
      console.log('handleOpenAiPanel called with event:', event);
      const detail = (event as CustomEvent<{ tabId?: string; context?: Array<{ key: string; value: unknown }> }>).detail;
      const targetTabId = detail?.tabId?.trim() || activeNavTabId;
      if (detail?.context?.length) {
        claraContextOverrideRef.current = [
          ...claraContextOverrideRef.current.filter(
            ({ tabId }) => tabId !== targetTabId,
          ),
          { tabId: targetTabId, context: detail.context },
        ];
      }
      openAiPanelWithContext(detail?.context, targetTabId);
    }
    window.addEventListener('kci:openAiPanel', handleOpenAiPanel);
    return () => window.removeEventListener('kci:openAiPanel', handleOpenAiPanel);
  }, [activeNavTabId, openAiPanelWithContext]);

  useEffect(() => {
    const handleCloseAiPanel = () => {
      if (aiPanelOpen) {
        closeAiPanelForActiveTab();
      }
    };

    window.addEventListener('kci:closeAiPanel', handleCloseAiPanel);
    return () => window.removeEventListener('kci:closeAiPanel', handleCloseAiPanel);
  }, [aiPanelOpen, closeAiPanelForActiveTab]);

  // Belt-and-suspenders: if the user somehow ends up on the Clara AI module's
  // own full-page tab while the panel was already open for it (e.g. internal
  // navigation that doesn't go through the guarded open paths above), force
  // it closed rather than risk two <clara-ai-chat-element> instances at once.
  useEffect(() => {
    if (!isClaraAiModuleActive || !aiPanelOpen) return;

    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) closeAiPanelForActiveTab();
    });
    return () => {
      cancelled = true;
    };
  }, [aiPanelOpen, closeAiPanelForActiveTab, isClaraAiModuleActive]);

  // Set window flag when AIPanel opens/closes so aichat component knows
  useEffect(() => {
    (window as unknown as Record<string, unknown>).__CLARA_AIPANEL_OPEN__ =
      aiPanelOpen;
    (window as unknown as Record<string, unknown>).__CLARA_AI_FULL_PAGE__ =
      isClaraAiModuleActive;

    return () => {
      (window as unknown as Record<string, unknown>).__CLARA_AIPANEL_OPEN__ = false;
      (window as unknown as Record<string, unknown>).__CLARA_AI_FULL_PAGE__ = false;
    };
  }, [aiPanelOpen, isClaraAiModuleActive]);

  useEffect(() => {
    localStorage.setItem("kci.workspace.activeTabId", activeNavTabId);
  }, [activeNavTabId]);

  // Sync assetsPanelOpen when workspace page closes the panel
  useEffect(() => {
    const handlePanelClosed = () => setAssetsPanelOpen(false);
    window.addEventListener(
      WORKSPACE_ASSET_EVENTS.PanelClosed,
      handlePanelClosed,
    );
    return () =>
      window.removeEventListener(
        WORKSPACE_ASSET_EVENTS.PanelClosed,
        handlePanelClosed,
      );
  }, []);

  // Open file in a dedicated nav tab when WorkspacePage dispatches FileView
  useEffect(() => {
    const handleFileViewEvent = (evt: Event) => {
      const detail = (evt as CustomEvent<AssetFileViewPayload>).detail;
      if (!detail) return;
      fileViewerFilesRef.current.set(detail.tabId, detail.file);
      openOrFocusNavTab({
        id: detail.tabId,
        label: detail.label,
        removable: true,
        tabKind: "workspaces",
      });
      navigateToPage("tru-clara-app", detail.tabId);
    };
    window.addEventListener(
      WORKSPACE_ASSET_EVENTS.FileView,
      handleFileViewEvent as EventListener,
    );
    return () =>
      window.removeEventListener(
        WORKSPACE_ASSET_EVENTS.FileView,
        handleFileViewEvent as EventListener,
      );
  }, [openOrFocusNavTab, navigateToPage]);

  // Wrap tab-close to evict stale file-viewer entries before navigation fires.
  const handleFileViewerAwareTabClose = useCallback(
    (tabId: string) => {
      // Check the shared guard BEFORE any cleanup runs, so a deferred close
      // (Agent Builder unsaved changes) never partially tears down AI-panel/
      // fullscreen state for a tab that ends up staying open.
      if (tabId === activeNavTabId && isAgentBuilderNavGuardActive()) {
        queuePendingAgentBuilderAction({ kind: "close", tabId });
        return;
      }

      if (tabId.startsWith("file-view:")) {
        fileViewerFilesRef.current.delete(tabId);
      }

      const fullscreenTabForClosingOrigin =
        fullscreenAiTabByOriginTabId.get(tabId);

      if (fullscreenTabForClosingOrigin) {
        sessionStorage.removeItem(
          `kci.fullscreenAiSession.${fullscreenTabForClosingOrigin}`,
        );
        fullscreenOriginTabIdsRef.current.delete(fullscreenTabForClosingOrigin);
        claraContextOverrideRef.current =
          claraContextOverrideRef.current.filter(
            (override) => override.tabId !== fullscreenTabForClosingOrigin,
          );

        setFullscreenAiTabByOriginTabId((prev) => {
          if (!prev.has(tabId)) return prev;
          const next = new Map(prev);
          next.delete(tabId);
          return next;
        });

        handleNavTabClose(fullscreenTabForClosingOrigin);
      }

      const originTabId = fullscreenOriginTabIdsRef.current.get(tabId);
      if (originTabId) {
        fullscreenOriginTabIdsRef.current.delete(tabId);

        setFullscreenAiTabByOriginTabId((prev) => {
          if (!prev.has(originTabId)) return prev;
          const next = new Map(prev);
          next.delete(originTabId);
          return next;
        });
      }

      setAiPanelOpenTabIds((prev) => {
        if (!prev.has(tabId)) return prev;
        const next = new Set(prev);
        next.delete(tabId);
        return next;
      });

      setAiPanelCollapsedTabIds((prev) => {
        if (!prev.has(tabId)) return prev;
        const next = new Set(prev);
        next.delete(tabId);
        return next;
      });

      claraContextOverrideRef.current = claraContextOverrideRef.current.filter(
        (override) => override.tabId !== tabId,
      );

      handleNavTabClose(tabId);
    },
    [activeNavTabId, isAgentBuilderNavGuardActive, queuePendingAgentBuilderAction, fullscreenAiTabByOriginTabId, handleNavTabClose],
  );

  //open asset panel from nav-header button click.  This dispatches a WorkspaceAssetEvent to the WorkspacePage, which will open the panel and fetch the asset state/files.
  const toggleAssetsPanel = useCallback(() => {
    if (assetsPanelOpen) {
      setAssetsPanelOpen(false);
      window.dispatchEvent(new CustomEvent(WORKSPACE_ASSET_EVENTS.PanelClosed));
      return;
    }
    const tab = navTabs.find((t) => t.id === activeNavTabId) ?? null;
    const context: AssetRequestContext = {
      tabId: tab?.id ?? "",
      contextType: tab?.tabKind === "workspaces" ? "Workspace" : "Other",
    };

    const eventEnvelope = createAssetViewRequestedEnvelope(context, {
      source: "nav-header",
    });
    console.log("sending view event envelope:", eventEnvelope);
    // Persist for WorkspacePage to replay if it hasn't mounted yet
    localStorage.setItem(
      "kci.workspace.pendingViewRequest",
      JSON.stringify(eventEnvelope),
    );

    window.dispatchEvent(
      new CustomEvent(WORKSPACE_ASSET_EVENTS.ViewRequested, {
        detail: eventEnvelope,
      }),
    );
    setAssetsPanelOpen(true);
  }, [assetsPanelOpen, activeNavTabId, navTabs]);

  const toggleMilestonePanel = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent("kci:toggleMilestones", {
        detail: { tabId: activeNavTabId },
      }),
    );
  }, [activeNavTabId]);

  useEffect(() => {
    const handleMilestonePanelState = (event: Event) => {
      const detail = (event as CustomEvent<{ tabId?: string; open?: boolean }>).detail;
      if (detail?.tabId && detail.tabId !== activeNavTabId) return;
      setMilestonePanelOpen(detail?.open === true);
    };

    window.addEventListener('kci:milestones-panel-state', handleMilestonePanelState);
    return () => window.removeEventListener('kci:milestones-panel-state', handleMilestonePanelState);
  }, [activeNavTabId]);

  const toggleNotifPanel = useCallback(() => {
    setNotifPanelOpen((prev) => !prev);
    if (aiPanelOpen) closeAiPanelForActiveTab();
    if (feedbackModalOpen) setFeedbackModalOpen(false);
  }, [aiPanelOpen, closeAiPanelForActiveTab, feedbackModalOpen]);

  const openFeedbackModal = useCallback(() => {
    setFeedbackModalOpen(true);
    if (aiPanelOpen) closeAiPanelForActiveTab();
    if (notifPanelOpen) setNotifPanelOpen(false);
  }, [aiPanelOpen, closeAiPanelForActiveTab, notifPanelOpen]);

  const closeFeedbackModal = useCallback(() => {
    setFeedbackModalOpen(false);
  }, []);

  useEffect(() => {
    return kciEventBus.subscribe("kci.shell.toast", () => {
      triggerNotification(FEEDBACK_TOAST_ID);
    });
  }, [FEEDBACK_TOAST_ID]);

  const closeNotifPanel = useCallback(() => {
    setNotifPanelOpen(false);
  }, []);

  const closeUtilityPanel = useCallback(() => {
    setUtilityTab(null);
    setContextTabOverride(null);
  }, []);

  const openFileViewTabIds = useMemo(
    () => navTabs.filter((tab) => tab.id.startsWith("file-view:")).map((tab) => tab.id),
    [navTabs],
  );

  const fileViewerContextValue = useMemo(
    () => ({
      getFile: (tabId: string) => fileViewerFilesRef.current.get(tabId),
      openFileViewTabIds,
    }),
    [openFileViewTabIds],
  );

  const acceptLabel = t("disclaimer.accept");

  return (
    <div
      className="app-frame"
      role="application"
      aria-label={t("header.appname")}
    >
      <ErrorBoundary level="feature" componentName="AIPanel">
        <AIPanel
          isOpen={aiPanelOpen}
          onClose={closeAiPanelForActiveTab}
          collapsed={aiPanelCollapsed}
          onCollapsedChange={setAiPanelCollapsedForActiveTab}
          onOpenInNewTab={handleOpenAiInNewTab}
          sessionIdToLoad={aiPanelSessionId}
          onWidthChange={setAiPanelWidth}
          disclaimerText={footerData?.disclaimerText?.trim()}
        />
      </ErrorBoundary>
      {/* Mandatory first-login disclaimer — blocks the UI until accepted */}
      {!firstLoginDisclaimerLoading &&
        !hasAcknowledged &&
        firstLoginDisclaimerContent && (
          <DialogBox
            open
            title={firstLoginDisclaimerContent.title}
            disableOverlayClose
            size="md"
            variant="default"
            actions={[
              {
                label: firstLoginAcknowledging
                  ? `${acceptLabel}…`
                  : acceptLabel,
                variant: "primary",
                disabled: firstLoginAcknowledging,
                onClick: () => {
                  // Capture the flag before the async call so it can't be
                  // mutated between now and when the Promise resolves.
                  const disclaimerWasShownThisSession =
                    firstLoginDisclaimerShownRef.current;
                  // After the API acknowledgment resolves, fire the first-login
                  // event so the Angular workspace can start the workspace-chat
                  // tour (with normal validation — only if not previously
                  // completed).  The event is scoped to the very first load
                  // where the disclaimer dialog was actually displayed.
                  void Promise.resolve(acknowledgeFirstLoginDisclaimer())
                    .then(() => {
                      // Clear the pending flag so the Angular workspace is
                      // unblocked and can start tours.
                      sessionStorage.removeItem(
                        "kci.first-login-disclaimer.pending",
                      );
                      if (disclaimerWasShownThisSession) {
                        window.dispatchEvent(
                          new CustomEvent(
                            "kci-first-login-disclaimer-accepted",
                          ),
                        );
                      }
                    })
                    .catch(() => {
                      // Errors are already handled inside the hook; ignore here.
                    });
                },
              },
            ]}
          >
            {renderDisclaimerContent(firstLoginDisclaimerContent.content)}
          </DialogBox>
        )}
      {pendingAgentBuilderNavAction && (
        <DialogBox
          open
          title="Unsaved changes"
          titleIcon={
            <span className="material-symbols-outlined">warning</span>
          }
          size="md"
          variant="default"
          onClose={cancelAgentBuilderNavAction}
          actions={[
            { label: "No, Go Back", variant: "secondary", onClick: cancelAgentBuilderNavAction },
            { label: "Yes, Leave", variant: "primary", onClick: confirmAgentBuilderNavAction },
          ]}
        >
          You have made changes to the form and have not saved. Moving away will delete all changes. You cannot undo this action. Are you sure you want to navigate away?
        </DialogBox>
      )}
      <div 
        className={`kci-app-wrap${aiPanelOpen ? " kci-ask-ai" : ""}`}
        style={aiPanelOpen ? { 
          paddingRight: `${aiPanelWidth}px`,
          '--ai-panel-width': `${aiPanelWidth}px`
        } as React.CSSProperties : undefined}
      >
        <ErrorBoundary level="feature" componentName="MainHeader">
          <MainHeader
            onToggleAi={toggleAiPanel}
            actions={headerActions}
            onOpenFeedback={openFeedbackModal}
            onToggleNotifications={toggleNotifPanel}
            onCloseNotifications={closeNotifPanel}
            onStartWelcomeTutorial={handleStartLeftMenuTour}
            aiPanelOpen={aiPanelOpen}
            notifPanelOpen={notifPanelOpen}
          />
        </ErrorBoundary>

        <ErrorBoundary level="feature" componentName="NavHeader">
          <NavHeader
            t={t}
            onToggleAi={toggleAiPanel}
            onCloseAi={closeAiPanelForActiveTab}
            onToggleNotifications={toggleNotifPanel}
            aiPanelOpen={aiPanelOpen}
            hideAiSpark={isClaraAiModuleActive || isFullscreenAiPanelTab || hasFullscreenAiForActiveTab}
            notifPanelOpen={notifPanelOpen}
            activeUtilityTab={utilityTab}
            onUtilityTabChange={setUtilityTab}
            onToggleAssets={toggleAssetsPanel}
            assetsPanelOpen={assetsPanelOpen}
            onToggleMilestone={toggleMilestonePanel}
            milestonePanelOpen={milestonePanelOpen}
            onActiveTabChange={handleActiveNavTabChange}
            tabs={navTabs}
            activeTabId={activeNavTabId}
            recentTabs={recentNavTabs}
            collapsedGroupIds={collapsedGroupIds}
            onTabActivate={activateNavTab}
            onTabClose={handleFileViewerAwareTabClose}
            onTabAddAction={handleNavAddAction}
            onAddMenuOpened={handleNewRecentTabsTourTrigger}
            onRecentTabOpen={handleRecentNavTabOpen}
            onRecentTabPinToggle={handleRecentNavTabPinToggle}
            onRecentTabAction={handleRecentNavTabActionWithSync}
            searchShareRecipients={lookupUserEmails}
            loadSharedRecipientIds={(entry) =>
              getAgentSharedUserIds(entry.agentId ?? entry.id.replace(/^agent:/, ""))
            }
            allowAgentSharing={allowAgentSharing}
            onToggleGroupCollapse={onToggleGroupCollapse}
          />
        </ErrorBoundary>

        <div className="app-shell__flex_wrap">
          <div className={`app-shell__body${isFullscreenAiPanelTab ? " ask-kci-expanded" : ""}`} role="main">
            <ErrorBoundary level="feature" componentName="LeftMenu">
              <div onClickCapture={handleLeftMenuClickCapture}>
                <LeftMenu
                  buttons={dockButtonDefs}
                  onButtonClick={handleDockButtonClick}
                />
              </div>
            </ErrorBoundary>
            <div className="body-content-wrap">
              <ErrorBoundary level="feature" componentName="BodyContent">
                <FileViewerContext.Provider value={fileViewerContextValue}>
                  <BodyContent />
                </FileViewerContext.Provider>
              </ErrorBoundary>
            </div>
            <ErrorBoundary level="feature" componentName="DockFlyout">
              <DockFlyout controller={dockFlyout} />
            </ErrorBoundary>
            <TourOverlay />
            <ErrorBoundary level="feature" componentName="UtilityPanel">
              <UtilityPanel
                mode={effectiveMode}
                onClose={closeUtilityPanel}
                contextTabOverride={contextTabOverride}
              />
            </ErrorBoundary>            
            <ErrorBoundary level="feature" componentName="FeedbackModal">
              {feedbackModalOpen && (
                <FeedbackModal onClose={closeFeedbackModal} />
              )}
            </ErrorBoundary>
          </div>

          {isStartPage && (
            <ErrorBoundary level="feature" componentName="AppFooter">
              <AppFooter
                disclaimerText={footerData?.disclaimerText?.trim()}
                copyRight={footerData?.copyRight}
                footerText={footerData?.footerText ?? fallbackFooterText}
                footerText1={footerData?.footerText1}
                footerLinkText1={footerData?.footerLinkText1}
                footerLinkURL1={footerData?.footerLinkURL1}
                versionText={environment.FooterVersion}
              />
            </ErrorBoundary>
          )}

          <ErrorBoundary level="feature" componentName="NotificationPanel">
            <NotificationPanel
              isOpen={notifPanelOpen}
              onClose={closeNotifPanel}
            />
          </ErrorBoundary>

          <Notification
            id={FEEDBACK_TOAST_ID}
            type="success"
            state="default"
            title="Feedback submitted successfully."
            placement="top-right"
            ctaClick={() => {}}
          />
          <Notification
            id={AGENT_SHARE_TOAST_ID}
            type="success"
            state="default"
            title="Agent has been shared"
            placement="top-right"
            ctaClick={() => {}}
          />
          <Notification
            id={RECENT_TAB_RENAME_TOAST_ID}
            type="success"
            state="default"
            title="Task renamed successfully"
            placement="top-right"
            ctaClick={() => {}}
          />
        </div>
      </div>
    </div>
  );
};

export default AppShell;
